import { ChangeEvent } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Control, useController, FieldValues, Path } from 'react-hook-form';
import styled, { CSSProperties, css } from 'styled-components';

export type RichTextFieldProps<T extends FieldValues> = {
  requiredIcon?: boolean;
  label?: string;
  name: Path<T>;
  control: Control<T>;
  directionLabel?: 'horizontal' | 'vertical';
  labelStyle?: CSSProperties;
  onChange?: (event: ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur?: (event: ChangeEvent<HTMLTextAreaElement>) => void;
  value?: string;
  disabled?: boolean;
};

interface StyledWrapperProps {
  directionLabel: 'horizontal' | 'vertical';
}

const StyledContainer = styled.div`
  .ql-container {
    border-radius: 6px;
    min-height: 200px;
    border: 1px solid var(--cl_neutral300);
    ${(props: { disabled?: boolean }) =>
      props.disabled &&
      css`
        background-color: #f5f5f5;
        cursor: not-allowed;
      `}
  }
  .ql-toolbar {
    ${(props: { disabled?: boolean }) =>
      props.disabled &&
      css`
        display: none;
      `}
  }
`;

const StyledWrapper = styled.div`
  ${(props: StyledWrapperProps) =>
    props?.directionLabel === 'horizontal' &&
    css`
      display: flex;
      align-items: center;
      gap: 8px;
    `}
`;

const StyledLabel = styled.div`
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: var(--cl_gray900);
  ${(props: StyledWrapperProps) =>
    props?.directionLabel === 'vertical' &&
    css`
      margin-bottom: 6px;
    `}
`;

const StyledErrorMessage = styled.div`
  color: var(--cl_error500);
  margin-top: 6px;
  font-size: 12px;
  line-height: 16px;
  font-weight: 400;
`;

export function RichTextField<T extends FieldValues>({
  label,
  name,
  control,
  requiredIcon = false,
  directionLabel = 'vertical',
  labelStyle,
  onChange: externalOnChange,
  onBlur: externalOnBlur,
  value: externalValue,
  disabled = false,
  ...rest
}: RichTextFieldProps<T>) {
  const {
    field: { onBlur, onChange, value, ref },
    fieldState: { error },
  } = useController({
    name,
    control,
  });

  return (
    <StyledContainer disabled={disabled}>
      <StyledWrapper directionLabel={directionLabel}>
        {label && (
          <StyledLabel style={labelStyle} directionLabel={directionLabel}>
            {label}
          </StyledLabel>
        )}

        <ReactQuill
          value={value}
          ref={ref}
          readOnly={disabled}
          onChange={(content, delta, source, editor) => {
            const event = { target: { value: content } } as ChangeEvent<HTMLTextAreaElement>;
            onChange(event);
            externalOnChange?.(event);
          }}
          onBlur={() => {
            const event = { target: { value } } as ChangeEvent<HTMLTextAreaElement>;
            onBlur(event);
            externalOnBlur?.(event);
          }}
          {...rest}
        />
      </StyledWrapper>

      {error?.message && <StyledErrorMessage>{error?.message}</StyledErrorMessage>}
    </StyledContainer>
  );
}
